/**
 * WebSocket 服务 - 单例模式
 * 负责 WebSocket 连接、消息队列、自动重连
 * 匹配后端 WebSocket 协议: /api/v1/chat/ws
 */

import { useChatStore } from '../stores/chatStore';
import { getCookie } from '../utils/cookie';
import { resolveWebSocketUrl } from './runtimeApiConfig';
import type {
  WSMessage,
  WSChatRequest,
  WSResponseMessage,
  WSResponseStreamChunk,
  WSResponseEnd,
  WSErrorMessage,
} from './types';

class WebSocketService {
  private static instance: WebSocketService;
  private ws: WebSocket | null = null;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private messageQueue: string[] = [];
  private currentUrl?: string;
  private currentConversationId: string | null = null;
  private streamingMessageId: string | null = null;

  private constructor() { }

  static getInstance(): WebSocketService {
    if (!WebSocketService.instance) {
      WebSocketService.instance = new WebSocketService();
    }
    return WebSocketService.instance;
  }

  /**
   * 连接 WebSocket
   * 连接前确保 anonymous_id cookie 存在
   * @param url WebSocket 服务器地址
   */
  async connect(url?: string) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      console.log('[WebSocket] 已连接，跳过');
      return;
    }

    this.currentUrl = url;
    const wsUrl = resolveWebSocketUrl(url);
    console.log('[WebSocket] 正在连接...', wsUrl);

    // 确保 anonymous_id cookie 存在，避免会话无法关联到匿名用户
    const { ensureAnonymousId } = await import('../utils/anonymous');
    const ensuredAnonymousId = await ensureAnonymousId();

    // 仅附加匿名会话标识，避免在 URL 中暴露后台认证状态
    const params = new URLSearchParams();
    const anonymousId = ensuredAnonymousId || getCookie('anonymous_id');
    if (anonymousId) {
      params.set('anonymous_id', anonymousId);
    }
    const separator = wsUrl.includes('?') ? '&' : '?';
    const finalWsUrl = params.toString() ? `${wsUrl}${separator}${params.toString()}` : wsUrl;

    this.ws = new WebSocket(finalWsUrl);

    this.ws.onopen = () => {
      console.log('[WebSocket] 已连接');
      useChatStore.getState().setConnected(true);
      this.flushQueue();
    };

    this.ws.onmessage = (event) => {
      this.handleMessage(event.data);
    };

    this.ws.onclose = () => {
      console.log('[WebSocket] 已断开');
      useChatStore.getState().setConnected(false);
      this.scheduleReconnect();
    };

    this.ws.onerror = (error) => {
      console.error('[WebSocket] 错误:', error);
    };
  }

  /**
   * 发送消息
   * @param content 消息内容
   * @param conversationId 会话 ID
   * @param stream 是否流式响应
   */
  async send(content: string, conversationId?: string, stream: boolean = false): Promise<void> {
    return new Promise((resolve) => {
      const request: WSChatRequest = {
        type: 'chat',
        message: content,
        conversation_id: conversationId || this.currentConversationId || undefined,
        stream,
      };

      const message = JSON.stringify(request);

      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(message);
        resolve();
      } else {
        // 加入队列，等待连接后发送
        this.messageQueue.push(message);
        // 简单实现：等待连接后发送
        setTimeout(() => resolve(), 1000);
      }
    });
  }

  /**
   * 处理服务端消息
   * @param data 消息数据（JSON 字符串）
   */
  private handleMessage(data: string) {
    try {
      const message: WSMessage = JSON.parse(data);
      const store = useChatStore.getState();

      switch (message.type) {
        case 'response':
          // 完整响应
          this.handleFullResponse(message);
          break;

        case 'response_stream':
          // 流式片段
          this.handleStreamChunk(message);
          break;

        case 'response_end':
          // 响应结束
          this.handleStreamEnd(message);
          break;

        case 'typing_indicator':
          // 输入指示器
          if (message.is_typing !== undefined) {
            store.setTyping(message.is_typing);
          }
          break;

        case 'error':
          // 错误消息
          console.error('[WebSocket] 服务端错误:', message.error);
          this.handleError(message);
          break;

        case 'heartbeat_ack':
          // 心跳确认
          console.debug('[WebSocket] 心跳确认');
          break;

        default:
          console.warn('[WebSocket] 未知消息类型:', message);
      }
    } catch (error) {
      console.error('[WebSocket] 消息解析错误:', error, data);
    }
  }

  /**
   * 处理完整响应
   */
  private handleFullResponse(message: WSResponseMessage) {
    const store = useChatStore.getState();
    const assistantMessage: ChatMessage = {
      id: message.message_id || `msg-${Date.now()}-assistant`,
      role: 'assistant',
      content: message.reply,
      timestamp: new Date(message.timestamp),
      intent: message.intent,
      confidence: message.confidence,
      suggestedActions: message.suggested_actions || [],
    };

    store.addMessage(assistantMessage);
    store.setTyping(false);

    // 更新当前会话 ID（同时更新 store 和本地变量）
    if (message.conversation_id) {
      this.currentConversationId = message.conversation_id;
      store.setConversationId(message.conversation_id);
      // 同时更新 currentSessionId 以保持一致性
      if (!store.currentSessionId) {
        store.setCurrentSessionId?.(message.conversation_id);
      }
    }
  }

  /**
   * 处理流式片段
   */
  private handleStreamChunk(message: WSResponseStreamChunk) {
    const store = useChatStore.getState();

    // 如果是第一个片段，创建消息
    if (!this.streamingMessageId) {
      this.streamingMessageId = `msg-${Date.now()}-assistant`;
      const newMessage: ChatMessage = {
        id: this.streamingMessageId,
        role: 'assistant',
        content: message.chunk,
        timestamp: new Date(message.timestamp),
        isStreaming: true,
      };
      store.addMessage(newMessage);
    } else {
      // 更新消息内容
      store.updateStreamingMessage(this.streamingMessageId, message.chunk);
    }

    // 更新当前会话 ID（同时更新 store 和本地变量）
    if (message.conversation_id) {
      this.currentConversationId = message.conversation_id;
      store.setConversationId(message.conversation_id);
      // 同时更新 currentSessionId 以保持一致性
      if (!store.currentSessionId) {
        store.setCurrentSessionId?.(message.conversation_id);
      }
    }
  }

  /**
   * 处理流式结束
   */
  private handleStreamEnd(message: WSResponseEnd) {
    const store = useChatStore.getState();

    if (this.streamingMessageId) {
      // 完成流式消息
      store.completeStreamingMessage(
        this.streamingMessageId,
        message.intent,
        message.confidence,
        message.suggested_actions || []
      );
      this.streamingMessageId = null;
    }

    store.setTyping(false);

    // 更新当前会话 ID（同时更新 store 和本地变量）
    if (message.conversation_id) {
      this.currentConversationId = message.conversation_id;
      store.setConversationId(message.conversation_id);
      // 同时更新 currentSessionId 以保持一致性
      if (!store.currentSessionId) {
        store.setCurrentSessionId?.(message.conversation_id);
      }
    }
  }

  /**
   * 处理错误消息
   */
  private handleError(message: WSErrorMessage) {
    const store = useChatStore.getState();

    // 不在对话列表中显示错误消息，仅记录日志并恢复状态
    console.error('[WebSocket] 后端错误（已静默处理）:', message.error || '未知错误');
    store.setTyping(false);
  }

  /**
   * 安排重连（3 秒后）
   */
  private scheduleReconnect() {
    if (this.reconnectTimer) return;

    this.reconnectTimer = setTimeout(() => {
      console.log('[WebSocket] 尝试重连...');
      this.reconnectTimer = null;
      void this.connect(this.currentUrl).catch((error) => {
        console.error('[WebSocket] 重连失败:', error);
      });
    }, 3000);
  }

  /**
   * 发送队列中的消息
   */
  private flushQueue() {
    while (this.messageQueue.length > 0 && this.ws?.readyState === WebSocket.OPEN) {
      const message = this.messageQueue.shift();
      if (message) {
        this.ws.send(message);
      }
    }
  }

  /**
   * 断开连接
   */
  disconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.ws?.close();
  }

  /**
   * 设置当前会话 ID
   */
  setConversationId(conversationId: string) {
    this.currentConversationId = conversationId;
  }

  /**
   * 获取当前会话 ID
   */
  getConversationId(): string | null {
    return this.currentConversationId;
  }
}

// 导出单例方法
export const connect = (url?: string) =>
  WebSocketService.getInstance().connect(url);

export const disconnect = () =>
  WebSocketService.getInstance().disconnect();

export const send = (content: string, conversationId?: string, stream?: boolean) =>
  WebSocketService.getInstance().send(content, conversationId, stream);

export const setConversationId = (conversationId: string) =>
  WebSocketService.getInstance().setConversationId(conversationId);

export const getConversationId = () =>
  WebSocketService.getInstance().getConversationId();

// 导入 ChatMessage 类型以避免循环依赖
interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
  intent?: string;
  confidence?: number;
  suggestedActions?: string[];
}
